import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Search, X, Plus, ChevronDown, Image as ImageIcon } from 'lucide-react';
import ShimmerImage from './ShimmerImage';

interface Option {
  value: string;
  label: string;
  subtitle?: string;
  level?: number;
  /**
   * Optional thumbnail (device hero shot, logo…). If ANY option carries one the
   * list reserves the thumbnail column for every row, so labels stay aligned
   * when only some options have a picture.
   */
  imageUrl?: string;
}

interface SearchableDropdownProps {
  options: Option[];
  value: string | null;
  onChange: (value: string | null) => void;
  onCreateNew?: (name: string) => void;
  createLabel?: string;
  placeholder?: string;
  label?: string;
  /** Accessible name for the trigger button when there is no visible `label`. */
  ariaLabel?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  compact?: boolean;
  variant?: 'light' | 'dark';
  buttonMono?: boolean;
}

export default function SearchableDropdown({
  options, value, onChange, onCreateNew, createLabel, placeholder = 'Select...',
  label, ariaLabel, searchPlaceholder = 'Search...', disabled = false, compact = false,
  variant = 'light', buttonMono = false,
}: SearchableDropdownProps) {
  const isDark = variant === 'dark';
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Position the portal dropdown below the button
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Reposition on external scroll, ignore scroll inside dropdown
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) return;
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownStyle((prev) => ({ ...prev, top: rect.bottom + 4, left: rect.left, width: rect.width }));
      }
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const filtered = options.filter(o => {
    if (search && o.value.startsWith('__group_')) return false; // hide group headers when searching
    return o.label.toLowerCase().includes(search.toLowerCase());
  });
  const selected = options.find(o => o.value === value);
  const hasLevels = options.some(o => (o.level ?? 0) > 0);
  const hasImages = options.some(o => !!o.imageUrl);

  /** Thumbnail cell — rendered for every row once any option has an image. */
  const Thumb: React.FC<{ src?: string; alt: string; size?: number }> = ({ src, alt, size = 28 }) => (
    <ShimmerImage
      src={src}
      alt={alt}
      width={size}
      height={size}
      objectFit="contain"
      className={`shrink-0 rounded-md border ${isDark ? 'border-dark-600' : 'border-stone-200'}`}
      fallbackBgColor={isDark ? 'bg-dark-700' : 'bg-stone-100'}
      fallbackIcon={<ImageIcon size={Math.round(size * 0.45)} className="text-stone-400" />}
      variant={variant}
      disableShimmer={!src}
    />
  );

  // Get the clean display name (strip indentation chars used in label)
  const getDisplayName = (option: Option) => option.label.replace(/^[\u00A0└ ]+/, '');

  const dropdownPanel = isOpen ? ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className={`${isDark ? 'bg-dark-800 border-dark-600' : 'bg-white border-gray-200'} border rounded-xl shadow-lg max-h-[320px] flex flex-col overflow-hidden`}
    >
      <div className={`flex items-center gap-2 px-3 border-b ${isDark ? 'border-dark-700' : 'border-gray-100'}`} tabIndex={-1}>
        <Search size={14} className={`${isDark ? 'text-stone-500' : 'text-stone-400'} flex-shrink-0`} />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className={`flex-1 bg-transparent text-[13px] py-2.5 ${isDark ? 'text-white placeholder-stone-500' : 'text-stone-900 placeholder-stone-400'}`}
          style={{ outline: 'none', boxShadow: 'none', border: 'none' }}
          autoFocus
        />
      </div>
      <div className="overflow-y-auto flex-1">
        {filtered.length === 0 ? (
          <div className={`px-3 py-3 text-[13px] text-center ${isDark ? 'text-stone-500' : 'text-stone-400'}`}>No matches</div>
        ) : (
          filtered.map(option => {
            const level = option.level ?? 0;
            const isSelected = option.value === value;
            const isRoot = level === 0;
            const hoverClass = isDark ? 'hover:bg-dark-700' : 'hover:bg-gray-50';
            const selectedBg = isDark ? 'bg-dark-700' : 'bg-gray-50';

            if (hasLevels) {
              const isGroupHeader = isRoot && option.value.startsWith('__group_');
              if (isGroupHeader) {
                return (
                  <div
                    key={option.value}
                    className={`px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider select-none ${isDark ? 'text-stone-500' : 'text-stone-400'}`}
                  >
                    {getDisplayName(option)}
                  </div>
                );
              }

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => { onChange(option.value); setIsOpen(false); setSearch(''); }}
                  className={`w-full flex items-center text-[13px] text-left transition-colors py-1.5 pr-3 ${hoverClass} ${
                    isSelected ? selectedBg : ''
                  }`}
                  style={{ paddingLeft: `${12 + level * 16}px` }}
                >
                  {level > 0 && !isRoot && (
                    <span className={`mr-1.5 flex-shrink-0 font-mono text-[11px] ${isDark ? 'text-stone-600' : 'text-stone-300'}`}>├─</span>
                  )}
                  {hasImages && <span className="mr-2 flex-shrink-0"><Thumb src={option.imageUrl} alt={getDisplayName(option)} size={24} /></span>}
                  <span className={
                    isSelected
                      ? `font-semibold ${isDark ? 'text-white' : 'text-stone-900'}`
                      : isRoot
                        ? `font-medium ${isDark ? 'text-white' : 'text-stone-900'}`
                        : (isDark ? 'text-stone-300' : 'text-stone-600')
                  }>
                    {getDisplayName(option)}
                  </span>
                  {option.subtitle && (
                    <span className={`ml-2 text-[11px] truncate ${isDark ? 'text-stone-500' : 'text-stone-400'}`}>{option.subtitle}</span>
                  )}
                </button>
              );
            }

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => { onChange(option.value); setIsOpen(false); setSearch(''); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-left transition-colors ${hoverClass} ${
                  isSelected
                    ? `${selectedBg} font-medium ${isDark ? 'text-white' : 'text-stone-900'}`
                    : (isDark ? 'text-stone-300' : 'text-stone-700')
                }`}
              >
                {hasImages && <Thumb src={option.imageUrl} alt={option.label} />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{option.label}</span>
                  {/* A subtitle passed by the caller used to be silently dropped
                      in this flat branch — it is the "what is this" line. */}
                  {option.subtitle && (
                    <span className={`block truncate text-[11px] ${isDark ? 'text-stone-500' : 'text-stone-400'}`}>{option.subtitle}</span>
                  )}
                </span>
              </button>
            );
          })
        )}
      </div>
      {onCreateNew && (
        <button
          type="button"
          onClick={() => {
            onCreateNew(search);
            setIsOpen(false);
            setSearch('');
          }}
          className={`flex items-center gap-2 px-3 py-2 border-t text-[13px] transition-colors ${isDark ? 'border-dark-700 text-blue-400 hover:bg-dark-700' : 'border-gray-100 text-blue-600 hover:bg-blue-50'}`}
        >
          <Plus size={14} />
          <span>{createLabel || (search ? `Create "${search}"` : 'Create new')}</span>
        </button>
      )}
    </div>,
    document.body
  ) : null;

  // Display selected value: show clean name without tree chars
  const selectedDisplay = selected ? getDisplayName(selected) : placeholder;

  const buttonBaseClasses = isDark
    ? 'bg-dark-700 hover:bg-dark-600 border-dark-600'
    : 'bg-white border-gray-300 hover:border-gray-400';
  const selectedTextClass = isDark ? 'text-white' : 'text-stone-900';
  const placeholderTextClass = isDark ? 'text-stone-500' : 'text-stone-400';
  const iconClass = isDark ? 'text-stone-400' : 'text-stone-400';
  const labelTextClass = isDark ? 'text-stone-300' : 'text-gray-700';
  const fontClass = buttonMono ? 'font-mono' : '';

  return (
    <div className="relative">
      {label && <label className={`block font-medium ${labelTextClass} ${compact ? 'text-xs mb-0.5' : 'text-sm mb-1'}`}>{label}</label>}
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between border text-left transition-colors disabled:opacity-50 ${buttonBaseClasses} ${compact ? 'px-2 py-1.5 rounded-md text-xs' : 'px-3 py-2 rounded-lg text-sm min-h-[44px]'}`}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2.5">
          {hasImages && selected?.imageUrl && <Thumb src={selected.imageUrl} alt={selectedDisplay} size={compact ? 20 : 28} />}
          <span className={`${fontClass} min-w-0 flex-1 truncate ${selected ? selectedTextClass : placeholderTextClass}`}>
            {selectedDisplay}
          </span>
        </span>
        <div className="flex items-center gap-1">
          {selected && (
            <span
              onClick={(e) => { e.stopPropagation(); onChange(null); }}
              className={`p-0.5 rounded ${isDark ? 'hover:bg-dark-700' : 'hover:bg-gray-100'}`}
            >
              <X size={14} className={iconClass} />
            </span>
          )}
          <ChevronDown size={16} className={iconClass} />
        </div>
      </button>
      {dropdownPanel}
    </div>
  );
}
